// DBT-298 (IVA unica, DEC-257): l'anteprima degli importi nel modulo della
// fattura usa la STESSA regola del server (backend/apps/fatture/calcolo_iva.py):
// un solo arrotondamento al centesimo, «metà in su» (DEC-255), come Fatture in
// Cloud (100,75 € al 22% → IVA 22,17, totale 122,92).
//
// I conti si fanno in centesimi INTERI: con i numeri con la virgola del browser
// 18,25 € al 22% (4,015) diventava 4,01 invece di 4,02.

// Testo dell'importo → centesimi interi, «metà in su» sul terzo decimale (come il
// database, che tiene due decimali). Un valore vuoto o non valido vale 0.
export function centesimi(valore) {
  const testo = String(valore == null ? '' : valore).trim().replace(',', '.');
  const parti = /^(\d*)(?:\.(\d*))?$/.exec(testo);
  if (!parti || (parti[1] === '' && !parti[2])) return 0;
  const decimali = (parti[2] || '').padEnd(3, '0');
  let risultato = Number(parti[1] || '0') * 100 + Number(decimali.slice(0, 2));
  if (Number(decimali[2]) >= 5) risultato += 1;
  return risultato;
}

// IVA in centesimi di un imponibile in centesimi, «metà in su».
export function ivaCentesimi(imponibileCentesimi, aliquota) {
  const percentuale = Math.round(Number(aliquota)) || 0;
  return Math.floor((imponibileCentesimi * percentuale + 50) / 100);
}

// Importi dell'anteprima in euro (solo per mostrarli: quelli autorevoli li
// calcola il server).
export function importiAnteprima(imponibile, aliquota) {
  const base = centesimi(imponibile);
  const iva = ivaCentesimi(base, aliquota);
  return { imponibile: base / 100, iva: iva / 100, totale: (base + iva) / 100 };
}

// DBT-311 (MODIFICA-ALIQUOTA-22, DEC-285) — le aliquote del menu del modulo della fattura.
export const ALIQUOTE_FATTURA = [0, 4, 10, 22];

// Le voci del menu dell'aliquota. In modifica il menu parte dall'aliquota VERA della bozza,
// quella del dettaglio (`aliquota_iva`): se è fuori elenco compare come «N% (salvata)», così
// salvare senza toccarla non la cambia; se dagli importi non si ricava (null) compare
// «— scegli», mai un'aliquota indovinata. Valori come testo, come quelli del <select>.
export function opzioniAliquotaFattura(salvata) {
  const voci = ALIQUOTE_FATTURA.map((a) => ({ v: String(a), l: a + '%' }));
  if (salvata === null) return [{ v: '', l: '— scegli' }].concat(voci);
  if (salvata === undefined || salvata === '') return voci;
  if (!ALIQUOTE_FATTURA.includes(Number(salvata))) voci.push({ v: String(salvata), l: salvata + '% (salvata)' });
  return voci;
}

// Il modulo di partenza: in modifica, se il dettaglio non porta l'aliquota (per esempio la
// riga della lista, quando il dettaglio non si carica) l'aliquota resta vuota invece del 22%
// del modulo nuovo, così «Salva» non la cambia.
export function moduloIniziale(vuoto, iniziale) {
  const modulo = { ...vuoto, ...iniziale };
  if (iniziale && iniziale.id && !('aliquota_iva' in iniziale)) modulo.aliquota_iva = null;
  return modulo;
}

// DEC-285 Q2 (a): finché imponibile e aliquota sono quelli della bozza, l'anteprima mostra i
// totali SALVATI (una bozza «IVA inclusa» da 200,00 resta 200,00, non i 199,99 ricalcolati);
// appena l'artigiano ne cambia uno, il calcolo di sempre.
export function importiAnteprimaModifica(form, iniziale) {
  const intatta = iniziale && iniziale.id && iniziale.iva_totale != null
    && centesimi(form.imponibile) === centesimi(iniziale.imponibile)
    && String(form.aliquota_iva ?? '') === String(iniziale.aliquota_iva ?? '');
  if (!intatta) return importiAnteprima(form.imponibile, form.aliquota_iva);
  const base = centesimi(iniziale.imponibile);
  const iva = centesimi(iniziale.iva_totale);
  return { imponibile: base / 100, iva: iva / 100, totale: (base + iva) / 100 };
}

// In modifica «Salva» non rimanda imponibile e aliquota se sono quelli della bozza: la regola
// vera sta sul server (`_aggiorna`), ma così nemmeno un server che non la conosce ancora (per
// esempio nei minuti del rilascio) tocca gli importi di una bozza non modificata.
export function senzaImportiInvariati(payload, iniziale) {
  if (!iniziale || !iniziale.id) return payload;
  const p = { ...payload };
  if (centesimi(p.imponibile) === centesimi(iniziale.imponibile)) delete p.imponibile;
  if (String(p.aliquota_iva ?? '') === String(iniziale.aliquota_iva ?? '')) delete p.aliquota_iva;
  return p;
}

// V2-16 S1: il messaggio dell'avviso quando «Salva» fallisce. Solo i testi leggibili del server
// (per esempio Q3 «Scegli l'aliquota IVA…» e Q4 sulla quantità), mai l'oggetto JSON grezzo né
// i codici per le macchine (`codice`, `campi`); senza testi leggibili, una frase generica.
const MSG_SALVATAGGIO_GENERICO = 'Impossibile salvare la fattura. Controlla la connessione e riprova.';
const CHIAVI_TECNICHE = ['codice', 'campi'];

function testiLeggibili(valore) {
  if (typeof valore === 'string') return valore.trim() && !valore.trim().startsWith('<') ? [valore.trim()] : [];
  if (Array.isArray(valore)) return valore.flatMap(testiLeggibili);
  if (valore && typeof valore === 'object') {
    return Object.keys(valore).filter((k) => !CHIAVI_TECNICHE.includes(k)).flatMap((k) => testiLeggibili(valore[k]));
  }
  return [];
}

export function messaggioErroreSalvataggio(errore) {
  const testi = testiLeggibili(errore && errore.response ? errore.response.data : null);
  return testi.length ? testi.join(' ') : MSG_SALVATAGGIO_GENERICO;
}
